import { memo, type ReactNode, useCallback, useMemo } from "react";

import { type TechnicalDetailsDataEntry } from "~/features/productDetails/dto/PdpPageProductDataDto";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { useIsMinDesktopSize, useIsMinDesktopSizeL, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { filterTechnicalDetails } from "~/domains/userPreferences/utils/unitLists";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { type ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { renderTechValue } from "~/utils/renderTechValue";
import { Accordion, AccordionSummary, Typography } from "@schaeffler-commerce/design-system";
import { PCAccordionDetails, PCTableContainer } from "./ComparisionTableStyles";
import parse from "html-react-parser";

interface ProductInformationTableProps {
  products: ProductDescriptor<PdpPageProductData>[];
  placeholders: number[];
  showOnlyDifference: boolean;
}

const getKey = (data: TechnicalDetailsDataEntry) => data.code?.substring(data.code?.lastIndexOf(".") ?? 0) ?? "";

type TechnicalDetailLabelProps = Pick<TechnicalDetailsDataEntry, "abbreviation" | "abbreviationAddition"> & {
  description: string;
};

export const TechnicalDetailLabel = ({
  abbreviation,
  abbreviationAddition,
  description,
}: TechnicalDetailLabelProps) => {
  const showDash = !!((abbreviation || abbreviationAddition) && description);

  return (
    <span>
      {parse(abbreviation ?? "")}
      {abbreviationAddition && <sub>{parse(abbreviationAddition)}</sub>}
      {showDash && " - "}
      {description}
    </span>
  );
};

const ProductInformationTable = memo(({ products, placeholders, showOnlyDifference }: ProductInformationTableProps) => {
  const isTablet = useIsMinTabletSize();
  const isDesktopL = useIsMinDesktopSizeL();
  const isDesktop = useIsMinDesktopSize();
  const { t } = useTranslation();
  const unitType = useUnitMode();

  const getDescription = useCallback(
    (data: TechnicalDetailsDataEntry) => (data.needsLocalisation ? t(data.description) : data.description),
    [t],
  );

  const rowDefinitions = useMemo(() => {
    const rowDefinitions: { uid: string; name: ReactNode }[] = [];
    const usedCodes: { [key: string]: boolean } = {};
    products.forEach((descriptor) => {
      const filteredTechDetails = descriptor?.base?.technicalDetails.map((item) =>
        filterTechnicalDetails(item, unitType),
      );
      filteredTechDetails.forEach((techDetail) => {
        techDetail.entries.forEach((row) => {
          const key = getKey(row);
          if (!usedCodes[key]) {
            const description = getDescription(row);
            rowDefinitions.push({
              uid: key,
              name: <TechnicalDetailLabel {...row} description={description} />,
            });
            usedCodes[key] = true;
          }
        });
      });
    });

    if (!showOnlyDifference) {
      return rowDefinitions;
    }

    return rowDefinitions.filter((rowDef) => {
      const values = products.map((product) => {
        const data = fetchDataForKey(product?.base, rowDef.uid);

        if (!data || !data.values.length) {
          return "";
        }

        return JSON.stringify({
          values: data.values,
          unit: data.unitSymbol,
        });
      });

      return new Set(values).size > 1;
    });
  }, [getDescription, products, unitType, showOnlyDifference]);

  const fakeData = useMemo(() => {
    let fakeData = new Array(rowDefinitions.length).fill(0);
    fakeData = fakeData.map((_, index) => ({ index }));
    return fakeData;
  }, [rowDefinitions]);

  const columnConfig: ColumnDef<{ index: number }>[] = useMemo(() => {
    const columnConfig: ColumnDef<{ index: number }>[] = [];

    const labelCol: ColumnDef<{ index: number }> = {
      id: "label",
      size: 246,
      minSize: 246,
      maxSize: 246,
      cell: (cell) => {
        const rowIndex = cell.row.original.index;
        return rowDefinitions[rowIndex]?.name;
      },
    };

    const makeProductCol = (index: number) => {
      const productCol: ColumnDef<{ index: number }> = {
        id: `product-${index}`,
        header: () =>
          !isTablet && index === 0 ? (
            <div style={{ marginLeft: "10px" }}>{t("productDetailsPage.tabs.information") as string}</div>
          ) : (
            ""
          ),
        size: 250,
        cell: (cell) => {
          const rowIndex = cell.row.original.index;
          const product = products[index];
          const rowDef = rowDefinitions[rowIndex];
          const data = fetchDataForKey(product?.base, rowDef?.uid);

          if (!data || !data.values.length) {
            return "";
          }

          const description = getDescription(data);

          return (
            <>
              {data.values.map((value, idx) => (
                <div key={idx} className={data.isConverted ? "f-info-color" : undefined}>
                  {renderTechValue(value, data.unitSymbol === "in", data.isConverted)} <span>{data.unitSymbol}</span>
                  <sup>{data.valueFootnotes.length > 1 ? data.valueFootnotes.join(", ") : data.valueFootnotes}</sup>
                  {!isDesktop && (
                    <Typography variant="bodySmall" color="textSecondary">
                      <TechnicalDetailLabel {...data} description={description} />
                    </Typography>
                  )}
                </div>
              ))}
            </>
          );
        },
      };
      return productCol;
    };

    if (isDesktop) {
      columnConfig.push(labelCol);
    }
    products.forEach((product, index) => {
      columnConfig.push(makeProductCol(index));
    });
    placeholders.forEach((product) => {
      columnConfig.push(makeProductCol(product));
    });

    if (isDesktopL) {
      const len = columnConfig.length;
      columnConfig[len - 1].size = undefined;
    }

    return columnConfig;
  }, [getDescription, isDesktop, isDesktopL, isTablet, placeholders, products, rowDefinitions, t]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography>{t("productDetailsPage.tabs.information")}</Typography>
      </AccordionSummary>
      <PCAccordionDetails>
        <PCTableContainer>
          <TanStackTable
            key={products.length}
            data={fakeData}
            columns={columnConfig}
            variant={"productComparison"}
            state={{
              columnPinning: {
                left: ["label"],
              },
            }}
          />
        </PCTableContainer>
      </PCAccordionDetails>
    </Accordion>
  );
});

const fetchDataForKey = (product: PdpPageProductData, key: string) => {
  return product?.technicalDetails.flatMap((table) => table.entries).find((row) => getKey(row) === key);
};

export default ProductInformationTable;
